/**
 * A body's own solar day, derived from the row's rotation and its orbit.
 *
 * `rotation.solar_day_s` is published for nine rows and null for the rest, and
 * the stand-in that used to fill the gap was `Math.abs(rotation.period_s)` -
 * the SIDEREAL day, which is 58.65 Earth days on Mercury against a solar day of
 * 175.94. Nothing about the difference is a table lookup: a solar day is the
 * beat between the body's own spin and the rate at which the SUN's direction
 * moves, and both are in the row already.
 *
 *     1 / T_solar = 1 / T_sidereal - 1 / T_orbit
 *
 * `period_s` is signed, negative for a retrograde rotator, so Venus's two terms
 * add rather than cancel and its 243-day spin makes a 116.75-day day; the
 * result is returned as a magnitude, since a day has no direction.
 *
 * T_orbit is the period of the body's HELIOCENTRIC motion, which for a
 * satellite is its PRIMARY's: the Sun's direction moves with the primary around
 * the Sun and barely at all with the moon around the primary. That is what
 * makes the Moon's solar day the 29.53-day synodic month off a 27.32-day spin,
 * and it is why the walk below climbs the `parent` chain to the first row whose
 * elements are not `parent_relative`.
 *
 * The check is `tools/atlas/solar_day.mjs --check`: every row that publishes a
 * solar day must be reproduced from its own rotation and orbit, and Mercury and
 * Jupiter - which publish none - must land on their measured figures.
 */

import type { BodyRow, Orbit } from './types';
import { DAYS_PER_CENTURY } from './ephemeris.ts';

const SECONDS_PER_DAY = 86400;

/**
 * An orbit's own period in seconds, from the mean longitude rate the registry
 * carries: `L_deg_cy` is 360 degrees per revolution per Julian century.
 */
export function orbitPeriodS(orbit: Orbit): number | null {
  const rate = orbit.rates.L_deg_cy;
  if (!Number.isFinite(rate) || rate === 0) return null;
  return Math.abs((360 / rate) * DAYS_PER_CENTURY * SECONDS_PER_DAY);
}

/**
 * The period of this row's motion around the SUN, seconds: its own where its
 * elements are heliocentric, and its primary's where they are not.
 */
export function heliocentricPeriodS(
  row: BodyRow,
  lookup: (id: string) => BodyRow | null | undefined,
): number | null {
  let r: BodyRow | null | undefined = row;
  // The chain is three deep at most in this registry; the bound is against a
  // row that names itself, or a cycle, rather than against the depth.
  for (let i = 0; i < 8 && r; i++) {
    if (r.orbit && !r.orbit.parent_relative) return orbitPeriodS(r.orbit);
    r = r.parent === null ? null : lookup(r.parent);
  }
  return null;
}

/**
 * The solar day this row's own rotation and orbit make, seconds, or null where
 * the row cannot supply one: Sol, which orbits nothing, and a row with no spin.
 */
export function derivedSolarDayS(
  row: BodyRow,
  lookup: (id: string) => BodyRow | null | undefined,
): number | null {
  const orbitS = heliocentricPeriodS(row, lookup);
  if (orbitS === null) return null;
  const siderealS = row.rotation.period_s;
  if (!Number.isFinite(siderealS) || siderealS === 0) return null;
  const rate = 1 / siderealS - 1 / orbitS;
  if (!Number.isFinite(rate) || rate === 0) return null;
  return Math.abs(1 / rate);
}

/**
 * The row's published solar day, else the one its rotation and orbit make.
 *
 * Null is an answer and not a gap to fill: on Sol there is no sun to return to
 * a meridian, and a clock scrubbed by a made-up day would be a lie about the
 * one thing the SOL row exists to show.
 */
export function solarDayS(
  row: BodyRow,
  lookup: (id: string) => BodyRow | null | undefined,
): number | null {
  return row.rotation.solar_day_s ?? derivedSolarDayS(row, lookup);
}
